import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faExpand,
  faCompress,
} from "@fortawesome/pro-light-svg-icons";
import Button from "../../../../buttons/Button";

const MonthlyView = ({
  date,
  items = [],
  onSelectDate,
  isFullscreen = false,
  onToggleFullscreen,
  tKey = "dashboard.bookings.calendar.",
}) => {
  const { t } = useTranslation();

  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();

  // Start week on Monday (1 = Mon, 0 = Sun → shift Sunday to 6)
  const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

  const weeks = [];
  let currentWeek = [];

  // Empty cells before first day
  for (let i = 0; i < startingDayOfWeek; i++) {
    currentWeek.push(null);
  }

  // All days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(new Date(year, month, day));
  }

  // Pad last week
  while (currentWeek.length < 7) {
    currentWeek.push(null);
  }
  weeks.push(currentWeek);

  const isCanceled = (item) =>
    item.displayStatus === "canceled" || item.displayStatus === "declined";

  const getItemsForDay = (day) => {
    if (!day) return [];
    const dateStr = day.toISOString().split("T")[0];
    return items.filter((item) => {
      const itemDate = new Date(item.bookingDate).toISOString().split("T")[0];
      return itemDate === dateStr;
    });
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day.toDateString() === today.toDateString();
  };

  // Monday-based week day labels
  const weekDays = [
    t(`${tKey}mon`),
    t(`${tKey}tue`),
    t(`${tKey}wed`),
    t(`${tKey}thu`),
    t(`${tKey}fri`),
    t(`${tKey}sat`),
    t(`${tKey}sun`),
  ];

  return (
    <div className="bookings-monthly">
      <div className="bookings-monthly__header bookings-view__header">
        <h4 className="bookings-monthly__title">
          {date.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          })}
        </h4>
        {onToggleFullscreen && (
          <Button variant="white" icon onClick={onToggleFullscreen}>
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </Button>
        )}
      </div>

      {/* Week day headers */}
      <div className="bookings-monthly__weekday-headers">
        {weekDays.map((day) => (
          <div key={day} className="bookings-monthly__weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="bookings-monthly__weeks">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="bookings-monthly__week">
            {week.map((day, dayIndex) => {
              const dayItems = getItemsForDay(day);
              const activeItems = dayItems.filter((item) => !isCanceled(item));
              const today = isToday(day);
              const totalGuests = activeItems.reduce(
                (sum, item) => sum + (item.partySize || 0),
                0,
              );
              const themes = [...new Set(activeItems.map((item) => item.colorTheme).filter(Boolean))];
              const dominantTheme = themes.length === 1 ? themes[0] : themes.length > 1 ? "mixed" : "danger";

              let cellClass = "bookings-monthly__day-cell";
              if (!day) cellClass += " bookings-monthly__day-cell--empty";
              else if (today) cellClass += " bookings-monthly__day-cell--today";

              return (
                <div
                  key={dayIndex}
                  className={cellClass}
                  onClick={() => day && onSelectDate(day)}
                >
                  {day && (
                    <>
                      <div className="bookings-monthly__day-number">
                        {day.getDate()}
                      </div>

                      {activeItems.length > 0 && (
                        <div>
                          <div
                            className={`bookings-monthly__day-indicator bookings-monthly__day-indicator--${dominantTheme}`}
                          >
                            {activeItems.length} {t(`${tKey}bookingsShort`)}
                          </div>

                          <div className="bookings-monthly__day-guests">
                            {totalGuests}{" "}
                            {t("dashboard.bookings.detail.guests")}
                          </div>

                          <div className="bookings-monthly__day-details-link">
                            <span>{t(`${tKey}details`)}</span>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthlyView;
